/**
 * jai1 tasks guide
 * Full usage guide - single source of truth for workflows and AI agents
 */
import { Command } from 'commander';
import chalk from 'chalk';

const GUIDE_TEXT = `
${chalk.cyan.bold('📖 Jai1 Task Management Guide')}

${chalk.bold('━━━ STATUSES ━━━')}
  ${chalk.dim('todo')}          📋  Chưa bắt đầu
  ${chalk.dim('in_progress')}   🔵  Đang làm (bao gồm review)
  ${chalk.dim('done')}          ✅  Hoàn thành
  ${chalk.dim('cancelled')}     ⚫  Huỷ
  ${chalk.dim('(blocked)')}     🔴  Computed: depends_on chưa done

${chalk.bold('━━━ PRIORITY ━━━')}
  ${chalk.dim('0')} = 🔥 Critical  — Prod down, security, block cả team
  ${chalk.dim('1')} = 🔴 High      — Feature chính, deadline gần
  ${chalk.dim('2')} = 🟡 Medium    — Bình thường (default)
  ${chalk.dim('3')} = 🟢 Low       — Nice-to-have, docs, refactor

${chalk.bold('━━━ QUICK START ━━━')}
  ${chalk.cyan('jai1 t add')} "Fix login bug" -p 1 -P bug/login
  ${chalk.cyan('jai1 t ready')}                    Show tasks sẵn sàng
  ${chalk.cyan('jai1 t pick')}                     Claim & start working
  ${chalk.cyan('jai1 t done')} T-003               Mark complete

${chalk.bold('━━━ DAILY WORKFLOW ━━━')}
  ${chalk.cyan('jai1 t sync --pull')}    Pull latest tasks
  ${chalk.cyan('jai1 t summary')}        Dashboard tổng quan
  ${chalk.cyan('jai1 t ready')}          Xem tasks sẵn sàng
  ${chalk.cyan('jai1 t pick')}           Claim task mới
  ${chalk.cyan('jai1 t done')} T-xxx     Hoàn thành task
  ${chalk.cyan('jai1 t sync --push')}    Push lên git

${chalk.bold('━━━ ADDING TASKS ━━━')}
  ${chalk.yellow('⚠ Luôn kiểm tra duplicate trước khi add:')}
    ${chalk.cyan('jai1 t list -P')} feature/xxx

  ${chalk.dim('Add cho feature:')}
    ${chalk.cyan('jai1 t add')} "Setup DB schema" -p 1 -P feature/xxx
    ${chalk.cyan('jai1 t add')} "Create API"      -p 1 -P feature/xxx
    ${chalk.cyan('jai1 t add')} "Build UI"        -p 2 -P feature/xxx

  ${chalk.dim('Add cho plan:')}
    ${chalk.cyan('jai1 t add')} "Refactor middleware" -p 2 -P plan/xxx

  ${chalk.dim('Add standalone:')}
    ${chalk.cyan('jai1 t add')} "Fix README typo" -p 3

  ${chalk.dim('Add bug fix:')}
    ${chalk.cyan('jai1 t add')} "Fix login redirect" -p 1 -P bug/xxx

${chalk.bold('━━━ DEPENDENCY ━━━')}
  ${chalk.dim('Task dependency:')}
    ${chalk.cyan('jai1 t dep')} T-002 T-001       T-002 chờ T-001 done
    ${chalk.cyan('jai1 t dep')} T-003 T-002       T-003 chờ T-002 done

  ${chalk.dim('Feature-level dependency:')}
    ${chalk.dim('# Nếu feature/auth phụ thuộc feature/user-model:')}
    ${chalk.cyan('jai1 t add')} "[DEP] Wait for feature/user-model" -p 1 -P feature/auth
    ${chalk.dim('# Rồi dep nó với tasks cuối của user-model')}

  ${chalk.dim('View deps:')}
    ${chalk.cyan('jai1 t show')} T-002             Hiện depends_on

${chalk.bold('━━━ TEAM COLLABORATION ━━━')}
  ${chalk.yellow('⚠ Assignment chỉ qua pick — không set thủ công.')}
  ${chalk.dim('Khi bạn pick → team thấy task đã có người nhận.')}

  ${chalk.dim('Sync morning:')}  ${chalk.cyan('jai1 t sync --pull')}
  ${chalk.dim('Sync evening:')}  ${chalk.cyan('jai1 t sync --push')}

${chalk.bold('━━━ FOR AI AGENTS (Workflow Integration) ━━━')}
  ${chalk.dim('Khi tạo tasks từ feature/plan:')}
  1. ${chalk.cyan('jai1 t list -P')} <parent>         Check existing (tránh duplicate)
  2. ${chalk.cyan('jai1 t add')} "..." -p <0-3> -P <parent>  Add từng task
  3. ${chalk.cyan('jai1 t dep')} <child> <parent>     Set dependencies
  4. ${chalk.cyan('jai1 t done')} <id>                Mark complete

  ${chalk.dim('Khi implement task tiếp theo:')}
  1. ${chalk.cyan('jai1 t pick')}  (hoặc ${chalk.cyan('jai1 t ready -P')} <parent>)
  2. Implement task
  3. ${chalk.cyan('jai1 t done')} <id>

  ${chalk.dim('Status transitions:')}
    add       → todo (default)
    pick      → in_progress (auto assign)
    done      → done
    update -s → any valid status

${chalk.bold('━━━ ALL COMMANDS ━━━')}
  ${chalk.cyan('jai1 t list')}     [-s status] [-P parent] [-j]
  ${chalk.cyan('jai1 t ready')}    [-P parent] [-j]
  ${chalk.cyan('jai1 t add')}      <title> [-p 0-3] [-P parent] [-t tags] [-j]
  ${chalk.cyan('jai1 t update')}   <id> [-s <status>] [-n <notes>] [-j]
  ${chalk.cyan('jai1 t show')}     <id|parent> [-j]
  ${chalk.cyan('jai1 t pick')}     [-j]
  ${chalk.cyan('jai1 t done')}     <id> [-j]
  ${chalk.cyan('jai1 t dep')}      <childId> <parentId> [-j]
  ${chalk.cyan('jai1 t sync')}     [--pull] [--push]
  ${chalk.cyan('jai1 t summary')}  [-j]
  ${chalk.cyan('jai1 t groups')}   [-s status] [-j]  ${chalk.dim('(alias: parents)')}
  ${chalk.cyan('jai1 t guide')}

  ${chalk.dim('-j / --json available on all commands (except guide, sync)')}
`;

export function createTaskGuideCommand(): Command {
  return new Command('guide')
    .description('Show full task management guide')
    .action(() => {
      console.log(GUIDE_TEXT);
    });
}
